/**
 * Assets Form Component
 */

import React, { useState } from 'react';
import { FormData, Property, Vehicle, CashAccount, Investment, RetirementAccount, OtherAsset } from '../types';

interface AssetsFormProps {
  formData: FormData;
  onChange: (field: keyof FormData, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

const AssetsForm: React.FC<AssetsFormProps> = ({ formData, onChange, onNext, onBack }) => {
  const [showRealEstateForm, setShowRealEstateForm] = useState(false);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [showCashForm, setShowCashForm] = useState(false);
  const [showInvestmentForm, setShowInvestmentForm] = useState(false);
  const [showRetirementForm, setShowRetirementForm] = useState(false);
  const [showOtherAssetForm, setShowOtherAssetForm] = useState(false);

  const assets = formData.assets || {};

  const addRealEstate = (property: Property) => {
    onChange('assets', {
      ...assets,
      realEstate: [...(assets.realEstate || []), property],
    });
    setShowRealEstateForm(false);
  };

  const addVehicle = (vehicle: Vehicle) => {
    onChange('assets', {
      ...assets,
      vehicles: [...(assets.vehicles || []), vehicle],
    });
    setShowVehicleForm(false);
  };

  const addCash = (account: CashAccount) => {
    onChange('assets', {
      ...assets,
      cash: [...(assets.cash || []), account],
    });
    setShowCashForm(false);
  };

  const addInvestment = (investment: Investment) => {
    onChange('assets', {
      ...assets,
      investments: [...(assets.investments || []), investment],
    });
    setShowInvestmentForm(false);
  };

  const addRetirement = (account: RetirementAccount) => {
    onChange('assets', {
      ...assets,
      retirement: [...(assets.retirement || []), account],
    });
    setShowRetirementForm(false);
  };

  const addOtherAsset = (asset: OtherAsset) => {
    onChange('assets', {
      ...assets,
      other: [...(assets.other || []), asset],
    });
    setShowOtherAssetForm(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Assets Information
      </h2>

      <p className="text-gray-600 mb-6">
        Please provide information about your assets. You can add multiple items in each category.
      </p>

      {/* Real Estate */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Real Estate</h3>
          {!showRealEstateForm && (
            <button
              onClick={() => setShowRealEstateForm(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + Add Property
            </button>
          )}
        </div>

        {assets.realEstate && assets.realEstate.length > 0 && (
          <div className="space-y-2 mb-4">
            {assets.realEstate.map((property, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="font-medium">{property.address}</div>
                <div className="text-sm text-gray-600">
                  Value: ${property.value.toLocaleString()} | Mortgage: ${property.remainingMortgage.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {showRealEstateForm && <RealEstateForm onSubmit={addRealEstate} onCancel={() => setShowRealEstateForm(false)} />}
      </div>

      {/* Vehicles */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Vehicles</h3>
          {!showVehicleForm && (
            <button
              onClick={() => setShowVehicleForm(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + Add Vehicle
            </button>
          )}
        </div>

        {assets.vehicles && assets.vehicles.length > 0 && (
          <div className="space-y-2 mb-4">
            {assets.vehicles.map((vehicle, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="font-medium">{vehicle.makeModel}</div>
                <div className="text-sm text-gray-600">
                  Value: ${vehicle.value.toLocaleString()} | Loan: ${vehicle.remainingLoan.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {showVehicleForm && <VehicleForm onSubmit={addVehicle} onCancel={() => setShowVehicleForm(false)} />}
      </div>

      {/* Cash & Bank Accounts */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Cash & Bank Accounts</h3>
          {!showCashForm && (
            <button
              onClick={() => setShowCashForm(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + Add Account
            </button>
          )}
        </div>

        {assets.cash && assets.cash.length > 0 && (
          <div className="space-y-2 mb-4">
            {assets.cash.map((account, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="font-medium">{account.accountType}</div>
                <div className="text-sm text-gray-600">
                  Amount: ${account.amount.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {showCashForm && <CashForm onSubmit={addCash} onCancel={() => setShowCashForm(false)} />}
      </div>

      {/* Investments */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Investments</h3>
          {!showInvestmentForm && (
            <button
              onClick={() => setShowInvestmentForm(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + Add Investment
            </button>
          )}
        </div>

        {assets.investments && assets.investments.length > 0 && (
          <div className="space-y-2 mb-4">
            {assets.investments.map((investment, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="font-medium">{investment.type}</div>
                <div className="text-sm text-gray-600">
                  Amount: ${investment.amount.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {showInvestmentForm && <InvestmentForm onSubmit={addInvestment} onCancel={() => setShowInvestmentForm(false)} />}
      </div>

      {/* Retirement Accounts */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Retirement Accounts</h3>
          {!showRetirementForm && (
            <button
              onClick={() => setShowRetirementForm(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + Add Account
            </button>
          )}
        </div>

        {assets.retirement && assets.retirement.length > 0 && (
          <div className="space-y-2 mb-4">
            {assets.retirement.map((account, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="font-medium">{account.accountType}</div>
                <div className="text-sm text-gray-600">
                  Amount: ${account.amount.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {showRetirementForm && <RetirementForm onSubmit={addRetirement} onCancel={() => setShowRetirementForm(false)} />}
      </div>

      {/* Other Assets */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Other Assets</h3>
          {!showOtherAssetForm && (
            <button
              onClick={() => setShowOtherAssetForm(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + Add Other Asset
            </button>
          )}
        </div>

        {assets.other && assets.other.length > 0 && (
          <div className="space-y-2 mb-4">
            {assets.other.map((asset, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="font-medium">{asset.description}</div>
                <div className="text-sm text-gray-600">
                  Value: ${asset.amount.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {showOtherAssetForm && <OtherAssetForm onSubmit={addOtherAsset} onCancel={() => setShowOtherAssetForm(false)} />}
      </div>

      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={onBack}
          className="bg-gray-200 text-gray-700 font-semibold py-3 px-8 rounded-lg hover:bg-gray-300 transition duration-300"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="bg-blue-600 text-white font-semibold py-3 px-8 rounded-lg hover:bg-blue-700 transition duration-300"
        >
          Next: Liabilities
        </button>
      </div>
    </div>
  );
};

// Sub-components for adding specific asset types
const RealEstateForm: React.FC<{ onSubmit: (property: Property) => void; onCancel: () => void }> = ({ onSubmit, onCancel }) => {
  const [property, setProperty] = useState<Property>({
    address: '',
    value: 0,
    remainingMortgage: 0,
    purchaseDate: '',
    paymentSource: 'post-marital',
  });

  const handleSubmit = () => {
    if (property.address && property.value > 0 && property.purchaseDate) {
      onSubmit(property);
    }
  };

  return (
    <div className="bg-blue-50 p-4 rounded-lg space-y-4">
      <input
        type="text"
        placeholder="Property Address"
        value={property.address}
        onChange={(e) => setProperty({ ...property, address: e.target.value })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <input
        type="number"
        placeholder="Current Value ($)"
        value={property.value || ''}
        onChange={(e) => setProperty({ ...property, value: Number(e.target.value) })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <input
        type="number"
        placeholder="Remaining Mortgage ($)"
        value={property.remainingMortgage || ''}
        onChange={(e) => setProperty({ ...property, remainingMortgage: Number(e.target.value) })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <input
        type="date"
        value={property.purchaseDate}
        onChange={(e) => setProperty({ ...property, purchaseDate: e.target.value })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <select
        value={property.paymentSource}
        onChange={(e) => setProperty({ ...property, paymentSource: e.target.value as 'pre-marital' | 'post-marital' })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      >
        <option value="post-marital">Purchased during marriage</option>
        <option value="pre-marital">Pre-marital property</option>
      </select>
      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
        >
          Add
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const VehicleForm: React.FC<{ onSubmit: (vehicle: Vehicle) => void; onCancel: () => void }> = ({ onSubmit, onCancel }) => {
  const [vehicle, setVehicle] = useState<Vehicle>({
    makeModel: '',
    value: 0,
    remainingLoan: 0,
  });

  return (
    <div className="bg-blue-50 p-4 rounded-lg space-y-4">
      <input
        type="text"
        placeholder="Make & Model (e.g., 2020 Toyota Camry)"
        value={vehicle.makeModel}
        onChange={(e) => setVehicle({ ...vehicle, makeModel: e.target.value })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <input
        type="number"
        placeholder="Current Value ($)"
        value={vehicle.value || ''}
        onChange={(e) => setVehicle({ ...vehicle, value: Number(e.target.value) })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <input
        type="number"
        placeholder="Remaining Loan ($)"
        value={vehicle.remainingLoan || ''}
        onChange={(e) => setVehicle({ ...vehicle, remainingLoan: Number(e.target.value) })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSubmit(vehicle)}
          className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
        >
          Add
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const CashForm: React.FC<{ onSubmit: (account: CashAccount) => void; onCancel: () => void }> = ({ onSubmit, onCancel }) => {
  const [account, setAccount] = useState<CashAccount>({
    accountType: '',
    amount: 0,
  });

  return (
    <div className="bg-blue-50 p-4 rounded-lg space-y-4">
      <input
        type="text"
        placeholder="Account Type (e.g., Savings, Checking)"
        value={account.accountType}
        onChange={(e) => setAccount({ ...account, accountType: e.target.value })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <input
        type="number"
        placeholder="Amount ($)"
        value={account.amount || ''}
        onChange={(e) => setAccount({ ...account, amount: Number(e.target.value) })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSubmit(account)}
          className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
        >
          Add
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const InvestmentForm: React.FC<{ onSubmit: (investment: Investment) => void; onCancel: () => void }> = ({ onSubmit, onCancel }) => {
  const [investment, setInvestment] = useState<Investment>({
    type: '',
    amount: 0,
  });

  return (
    <div className="bg-blue-50 p-4 rounded-lg space-y-4">
      <input
        type="text"
        placeholder="Investment Type (e.g., Stocks, ETF, Crypto)"
        value={investment.type}
        onChange={(e) => setInvestment({ ...investment, type: e.target.value })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <input
        type="number"
        placeholder="Amount ($)"
        value={investment.amount || ''}
        onChange={(e) => setInvestment({ ...investment, amount: Number(e.target.value) })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSubmit(investment)}
          className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
        >
          Add
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const RetirementForm: React.FC<{ onSubmit: (account: RetirementAccount) => void; onCancel: () => void }> = ({ onSubmit, onCancel }) => {
  const [account, setAccount] = useState<RetirementAccount>({
    accountType: '',
    amount: 0,
  });

  return (
    <div className="bg-blue-50 p-4 rounded-lg space-y-4">
      <select
        value={account.accountType}
        onChange={(e) => setAccount({ ...account, accountType: e.target.value })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      >
        <option value="">Select account type</option>
        <option value="401(k)">401(k)</option>
        <option value="IRA">IRA</option>
        <option value="Pension">Pension</option>
        <option value="Other">Other</option>
      </select>
      <input
        type="number"
        placeholder="Amount ($)"
        value={account.amount || ''}
        onChange={(e) => setAccount({ ...account, amount: Number(e.target.value) })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSubmit(account)}
          className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
        >
          Add
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const OtherAssetForm: React.FC<{ onSubmit: (asset: OtherAsset) => void; onCancel: () => void }> = ({ onSubmit, onCancel }) => {
  const [asset, setAsset] = useState<OtherAsset>({
    description: '',
    amount: 0,
  });

  return (
    <div className="bg-blue-50 p-4 rounded-lg space-y-4">
      <textarea
        placeholder="Description (e.g., Jewelry, Art, Collectibles)"
        value={asset.description}
        onChange={(e) => setAsset({ ...asset, description: e.target.value })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
        rows={3}
      />
      <input
        type="number"
        placeholder="Value ($)"
        value={asset.amount || ''}
        onChange={(e) => setAsset({ ...asset, amount: Number(e.target.value) })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSubmit(asset)}
          className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
        >
          Add
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AssetsForm;